import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNavigate } from 'react-router-dom';
import WalletTransition from './WalletTransition';

const isProductionDomain = typeof window !== 'undefined' && window.location.hostname === 'yobitch.fun';
const enterPath = isProductionDomain ? '/' : '/landing';
// DEV BYPASS: Skip gate on localhost
const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const WalletGate = ({ children }) => {
  const { connected } = useWallet();
  const navigate = useNavigate();
  const wasConnected = useRef(false);
  const [showTransition, setShowTransition] = useState(false);
  const [transitionComplete, setTransitionComplete] = useState(false);
  
  // Check for demo mode - SECURITY: Only allow in development OR if admin is logged in
  const urlParams = new URLSearchParams(window.location.search);
  const urlDemoMode = urlParams.get('demo') === 'true';
  const isAdminLoggedIn = localStorage.getItem('admin-logged-in') === 'true';
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Demo mode only works in development or for logged-in admins
  // Also bypass on localhost for easier development
  const canUseDemoMode = isDevelopment || isAdminLoggedIn || isLocalDev;
  
  if (urlDemoMode && canUseDemoMode && typeof window !== 'undefined') {
    localStorage.setItem('vault-demo-mode', 'true');
  }
  // On localhost, always enable demo mode to bypass wallet requirement
  const isDemoMode = isLocalDev || (canUseDemoMode && (urlDemoMode || localStorage.getItem('vault-demo-mode') === 'true'));

  // Handle transition completion
  const handleTransitionComplete = useCallback(() => {
    setShowTransition(false);
    setTransitionComplete(true);
  }, []);

  // Wallet connection transition
  useEffect(() => {
    if (connected && !wasConnected.current && !transitionComplete) {
      wasConnected.current = true;
      queueMicrotask(() => setShowTransition(true));
    } else if (connected && !wasConnected.current) {
      wasConnected.current = true;
    } else if (!connected && wasConnected.current && !isDemoMode) {
      wasConnected.current = false;
      queueMicrotask(() => setTransitionComplete(false));
      sessionStorage.removeItem('yb-authenticated');
      navigate(enterPath, { replace: true });
    }
  }, [connected, navigate, isDemoMode, transitionComplete]);

  // Redirect to landing if not connected (user must go through proper flow)
  useEffect(() => {
    if (!connected && !isDemoMode) {
      navigate(enterPath, { replace: true });
    }
  }, [connected, isDemoMode, navigate]);

  // Show transition screen after wallet connects
  if (showTransition) {
    return <WalletTransition onComplete={handleTransitionComplete} />;
  }

  // Show nothing while redirecting
  if (!connected && !isDemoMode) {
    return null;
  }

  return children;
};

export default WalletGate;
